define((require) => {
    "use strict";

    const {has, each, extend} = require("underscore");
    const Marionette          = require("marionette");
    const Permissions         = require("shared/security/Permissions");
    const Promise             = require("shared/util/Promise");
    const Time                = require("shared/services/Time");

    const DRAGOVER = "dragover.FileDropManager";
    const DROP = "drop.FileDropManager";

    /**
     * Abstract class for dealing with "dropped" file uploads.
     *
     * A manager that can be attached to an element to help encapsulate drop events.
     *
     * @see ImageDropManager
     * @see CustomFontDropManager
     *
     * @class FileDropManager
     */
    return Marionette.Controller.extend({

        fileModel: null,

        /**
         * Subclass hook invoked at upload time for providing additional model attributes.
         *
         * @param {String} name The raw filename.
         * @param {Number} size The file size, in bytes.
         *
         * @return {Object} Object with addional fields.
         *
         * @see _createPlaceholder
         */
        // eslint-disable-next-line no-unused-vars
        fileModelAttributes(name, size) {
            return {};
        },

        fileStorage: null,

        addElement(element) {
            element.on(DRAGOVER, this._dragoverHandler);
            element.on(DROP, this._dropHandler.bind(this));

            // For chainability
            return this;
        },

        removeElement(element) {
            element.off(DRAGOVER);
            element.off(DROP);
        },

        _dragoverHandler(event) {
            event.preventDefault();
        },

        /**
         * Handles the drop event, and starts the upload if
         * the event contains files.
         *
         * @param {Event} event
         * @private
         */
        _dropHandler(event) {
            const dataTransfer = event.originalEvent.dataTransfer;
            event.preventDefault();

            // Must have CREATE privilege...
            if (Permissions.Privileges.CREATE.can()) {
                // Dropped from desktop
                const descriptor = this.upload(dataTransfer);
                this.trigger("upload", descriptor);
            }
        },

        /**
         * Checks whether the dragged file comes from Creator (e.g.: is either a widget placeholder or an image from image library).
         *
         * @param {DataTransfer} dataTransfer from drag event.
         * @return {Boolean} True if the dragged file is either a placeholder or an image from Creator library.
         */
        isFileDraggedFromCreator(dataTransfer) {
            try {
                const data = JSON.parse(dataTransfer.getData("Text"));

                return Boolean(data) && has(data, "type");  // Because both placeholders and images have a type
            } catch (e) { // only on parse errors
                return false;
            }
        },

        /**
         * It normalizes an array of files from the data transfer object by adding zero-size files to the list.
         * This is needed because when dragging zipped files from the zip folder into Creator, the file size is set to zero
         * (as the file is not yet unzipped), causing the generation of empty models.
         *
         * File validation is propagated to the AbstractFileStorage class which handles the zero-size scenario,
         * along with all other file validations.
         *
         * @param {DataTransfer} dataTransfer from drag event.
         * @private
         *
         * @return {Files|Object[]} An Array of Files and/or Object. The object should have at least a name and size field
         *                          coming the the File API interface (other fields are not needed). It cannot be a File instance
         *                          because the File API constructor is not supported on all browsers.
         */
        _normalizeFilesFromDataTransfer(dataTransfer) {
            if (!dataTransfer) {
                return [];
            }

            const dataText = dataTransfer.getData && dataTransfer.getData("Text");
            const files = [];

            // Converting FileList type to array of files (and file-like objects). Mainly due because of IE11.
            each(dataTransfer.files, (file) => {
                files.push(file);
            });

            const modelId = dataText && dataText.split(" ")[1];

            // When dragging files from zip folders, you either get file with zero size or no files at all. Here we handle the latter by
            // creating a fake zero-size file, while the first case is just propagated to the File Storage validation.
            if (!modelId && files.length === 0 && !this.isFileDraggedFromCreator(dataTransfer)) {
                files.push({
                    size: 0,
                    name: "file"
                });
            }
            return files;
        },


        /**
         * An object describing the state of the current upload.
         *
         * @typedef {Object} UploadDescriptor
         * @property {Promise[]} promises - Array od promises to be triggered once upload is complete.
         * @property {Backbone.Model[]} models - Models to be created once upload is complete
         * @property {Object[]} descriptors - Object associating promises with models.
         */

        /**
         * Starts uploading of files and fires off a trigger including a
         * dropDescriptor with a promise which triggers once the file has been
         * uploaded.
         *
         * @param {File|Object[]} Array of file(s) from the drop event.
         *                       It also may contain objects respecting the File API interface, with at least name and size as mandatory fields.
         *                       This is due when handling edge case of dragging unzipped files directly from the zip folder, which may inject zero-size files.
         * @return {UploadDescriptor} An object holding information about the upload.
         * @public
         */
        upload(dataTransfer) {
            const promises = [];
            const descriptors = [];
            const models = [];

            this._normalizeFilesFromDataTransfer(dataTransfer).forEach((file) => {
                const promise = new this.fileStorage().storeFile(file);

                if (promise.state() !== Promise.State.REJECTED) {
                    const model = this._createPlaceholder(promise, file.name, file.size);

                    promises.push(promise);
                    models.push(model);

                    descriptors.push({
                        model,
                        promise
                    });
                }
            });

            const promise = Promise.all(promises);

            return {
                promise,
                models,
                descriptors
            };
        },

        /**
         * <p>Creates the "pseudo" model, that eventually gets replaced
         * by the real model.</p>
         *
         * <p>When the promise resolves (should pass in the model of the
         * newly uploaded file) it will remove the placeholder again and add
         * the real model.</p>
         *
         * @param {Promise} promise - Promise that will resolve passing in a
         *                            real model of the uploaded image.
         * @param {String} [name]   - The temporary name of the uploaded file.
         * @param {Number} [size]   - Size (in bytes) of the uploaded image.
         * @return {Backbone.Model} - Placeholder model.
         * @private
         */
        _createPlaceholder(promise, name, size) {
            name = name || "?";

            // By default, `createdAt`, `uploading` and `progress` are added to the model
            // at upload time. These are omitted once the upload is complete.
            const modelAttributes = extend({
                createdAt: Time.server(),
                progress:  0,
                uploading: true
            }, this.fileModelAttributes(name, size));

            // We make a "pseudo" model for the collection. The associated view is
            // required to be able to handle this type of model.
            const modelPlaceholder = new this.fileModel(modelAttributes);

            promise.progress((progress) => {
                modelPlaceholder.set("progress", progress);
            });

            promise
                // A successful upload resolves the promise with the
                // newly create model.
                .then((model) => {
                    modelPlaceholder.set(model.omit("createdAt"));
                    modelPlaceholder.unset("uploading");
                    modelPlaceholder.unset("progress");

                    this.trigger("file:uploaded", modelPlaceholder);
                })
                // If the promise fails, we will want to remove
                // the placeholder when the promise ends.
                .fail(() => {
                    modelPlaceholder.set("uploadFailed", true);
                    modelPlaceholder.trigger("upload:failed", modelPlaceholder);
                });

            return modelPlaceholder;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/services/FileDropManager.js